import * as React from 'react';
import {RiveState, useRive} from '@rive-app/react-canvas';
import {Button, Typography} from 'onboarding-components';
import produce from 'immer';

import {openDemoProjectSelectorModal} from 'common/widgets/demo-selector-modal';
import {
  AppContext,
  SetupGuideContext,
  Step,
  SubStep,
  UserEventPropertiesMapContext,
  VisibilityContext,
} from 'common/onboarding/immersive-onboarding/types';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {EventSubStepContext} from 'common/onboarding/immersive-onboarding/steps/events/common';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {ApiClient} from 'common/onboarding/immersive-onboarding/api-client';
import type {ChromeHeader} from 'common/widgets/chrome/chrome-header';
import {SummaryTable} from 'common/onboarding/immersive-onboarding/components/';
import {scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';

import styles from './ready-to-connect-v2.module.styl';

export const ReadyToConnectV2 = ({selected}) => {
  const {setVisibility} = React.useContext(VisibilityContext);
  const {currentUser} = React.useContext(AppContext);
  const {setSetupGuideInfo, setupGuideInfo, setStep} = React.useContext(SetupGuideContext);
  const {currentEventPropertiesMap} = React.useContext(UserEventPropertiesMapContext);

  const canInviteUsers = currentUser;

  const {rive, RiveComponent}: RiveState = useRive({
    src: IMMERSIVE_ASSETS.readyToConnect,
    stateMachines: [`State Machine 1`],
  });

  const {eventSubStep} = React.useContext(EventSubStepContext);

  // TODO: probably should use IntersectionObserver for this
  React.useEffect(() => {
    let timer;
    if (eventSubStep === SubStep.ReadyToConnect) {
      timer = setTimeout(() => {
        rive.play();
      }, 700);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [eventSubStep]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <SummaryTable
          onEdit={() => {
            scrollToStep(SubStep.EventConfirmation);
            track(TrackingEvent.SummaryTableEditClick, {[TrackingProperty.Phase2]: true});
          }}
          tableTitle='Your planned events'
        />
        <div className={styles.contentRight}>
          <RiveComponent style={{height: `300px`, width: `300px`}} />
          <div>
            <div className={styles.textContainer}>
              <Typography variant='captionSm' weight='bold'>
                Need help from a teammate?
              </Typography>
              <Typography className={styles.textBody} variant='captionSm'>
                Send your planned events and our 15 minute connection guide to a teammate
              </Typography>
            </div>
            <div>
              <Button
                className={styles.buttonPadding}
                variant='inline'
                includeArrow={false}
                size='small'
                onClick={() => {
                  if (canInviteUsers) {
                    setVisibility(false);

                    const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
                    chromeHeader.helpers.openInviteUserModal(() => setVisibility(true));
                  } else {
                    addToast({
                      type: ToastType.ERROR,
                      message: `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
                    });
                  }

                  track(TrackingEvent.InviteTeammate, {[TrackingProperty.Phase2]: true});
                }}
              >
                Invite a teammate
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div>
          <Button
            size='small'
            onClick={() => {
              setStep(Step.Implement);
              track(TrackingEvent.SubStepCompleted, {
                [TrackingProperty.SubStepName]: SubStep.ReadyToConnect,
                [TrackingProperty.Phase2]: true,
              });

              ApiClient.updateSetupGuideInfo(setupGuideInfo.projectId, selected, null, currentEventPropertiesMap).then(
                (result) => {
                  setSetupGuideInfo(result);
                },
              );

              const updatedStepsCompleted = produce(setupGuideInfo.stepsCompleted, (draft) => {
                draft.add(Step.Events);
              });
              ApiClient.updateCompleted(setupGuideInfo.projectId, updatedStepsCompleted);
              setSetupGuideInfo({...setupGuideInfo, stepsCompleted: updatedStepsCompleted});
            }}
          >
            Connect your data
          </Button>
        </div>
        <div>
          <Button
            variant='inline'
            size='small'
            includeArrow={false}
            onClick={() => {
              track(TrackingEvent.ViewDemoClick, {
                [TrackingProperty.SubStepName]: SubStep.ReadyToConnect,
                [TrackingProperty.Phase2]: true,
              });
              openDemoProjectSelectorModal(
                (ev) => {
                  addToast({message: ev.detail.message, type: ToastType.ERROR});
                },
                {'is-new-tab': true},
              );
            }}
          >
            View a demo project
          </Button>
        </div>
      </div>
    </div>
  );
};
